<template>
  <div class="mt-blog">
    <section class="mt-blog__cabecera">
      <div class="mt-contenedor">
        <p class="mt-etiqueta"><v-icon size="16">mdi-book-open-page-variant-outline</v-icon> Blog</p>
        <h1>{{ titulo }}</h1>
        <p class="mt-blog__sub">{{ subtitulo }}</p>
      </div>
    </section>

    <section class="mt-seccion mt-seccion--blog">
      <div class="mt-contenedor">
        <div v-if="entradas.length" class="mt-blog__filtros">
          <label class="mt-buscador">
            <v-icon size="20">mdi-magnify</v-icon>
            <input v-model="busqueda" type="search" placeholder="Buscar artículo" aria-label="Buscar artículo" />
          </label>
          <div v-if="categorias.length > 1" class="mt-blog__categorias" role="group" aria-label="Categorías">
            <button type="button" class="mt-chip" :class="{ activo: !categoria }" :aria-pressed="!categoria" @click="categoria = null">Todas</button>
            <button v-for="c in categorias" :key="c" type="button" class="mt-chip" :class="{ activo: categoria === c }"
              :aria-pressed="categoria === c" @click="categoria = categoria === c ? null : c">{{ c }}</button>
          </div>
        </div>

        <div v-if="visibles.length" class="mt-entradas">
          <MinotariaBlogTarjeta v-for="e in visibles" :key="e.id" :entrada="e" />
        </div>
        <p v-else class="mt-blog__vacio">
          {{ entradas.length ? 'No hay artículos que coincidan con la búsqueda.' : 'Todavía no hay artículos publicados.' }}
        </p>

        <div v-if="filtradas.length > visibles.length" class="mt-blog__mas">
          <button type="button" class="mt-boton mt-boton--borde" @click="limite += POR_PAGINA">Ver más artículos</button>
        </div>
      </div>
    </section>

    <MinotariaBlogCierre />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useStore } from '../../store/index'
import { useMinotariaBlog } from '../../composables/useMinotariaBlog'
import MinotariaBlogTarjeta from './MinotariaBlogTarjeta.vue'
import MinotariaBlogCierre from './MinotariaBlogCierre.vue'

const store = useStore()
const { entradas, categorias } = useMinotariaBlog()

// Título y subtítulo del blog del panel (SEO de la sección); si no hay, los de minotaria.es
const titulo = computed(() => store.NOTARIA?.Theme?.Blog?.Titulo?.Texto?.trim() || 'Guías para su trámite notarial')
const subtitulo = computed(() => store.NOTARIA?.Theme?.Blog?.Subtitulo?.Texto?.trim()
  || 'Herencias, compraventas, testamentos, poderes… Explicado de forma sencilla para que llegue a la notaría con todo claro.')

const POR_PAGINA = 9
const limite = ref(POR_PAGINA)
const busqueda = ref('')
const categoria = ref<string | null>(null)
watch([busqueda, categoria], () => { limite.value = POR_PAGINA })

const filtradas = computed(() => {
  const q = busqueda.value.trim().toLowerCase()
  return entradas.value.filter((e) =>
    (!categoria.value || e.Categorias?.includes(categoria.value)) &&
    (!q || [e.Titulo, e.Extracto, ...(e.Categorias || [])].some((t) => t?.toLowerCase().includes(q))))
})
const visibles = computed(() => filtradas.value.slice(0, limite.value))

// SEO de "Diseño páginas → Blog" sin useMetaTags, que añade los datos estructurados de una notaría (como en la portada)
if (store.NOTARIA) {
  const seo = store.NOTARIA.Theme?.Blog
  useSeoMeta({
    title: titulo.value,
    description: seo?.Description || subtitulo.value,
    keywords: seo?.Keywords || undefined,
    ogTitle: `${titulo.value} | ${store.NOTARIA.Nombre}`,
    ogDescription: seo?.Description || subtitulo.value,
    ogType: 'website',
    robots: 'index, follow',
  })
  useHead({ link: [{ rel: 'canonical', href: `https://${store.host}/blog` }] })
}
</script>
